/**
 * Solution Workload chart — Stories 1, 4, 5 of "Visualize Solution Workload"
 * (docs/specs/visualize-solution-workload.md). Hand-rolled SVG/CSS, mirroring
 * the FitRadar.tsx idiom (Implementation decision #7) — no chart library
 * exists in this stack and none may be added.
 *
 * Story 1: 13 monthly buckets (6 back / current / 6 forward). Months before
 * the current month render logged actuals; the current + forward months
 * render summed Guidance. A "today" divider marks the actuals→guidance
 * boundary, and the current month is explicitly labeled guidance-based.
 *
 * Story 4: the Intensity → expected-hours benchmark (display-only,
 * never fed back into Fit Score or points — see the firewall note below)
 * renders as an overlay across all 13 buckets, with under/over-served
 * buckets flagged when they deviate materially from the benchmark. The
 * benchmark's hours value is labeled directly on the line at each level.
 *
 * Story 5: a legend distinguishes the three series; a labeled hours Y-axis
 * gives the bars scale; and a per-bucket hover tooltip surfaces the month's
 * hours broken out by Resource, the active Intensity, and the benchmark,
 * with an inline caption stating the live Intensity → hours conversion.
 *
 * 🔒 Firewall: this component only ever *reads* `expectedHours` from the
 * payload for display. It never writes anything, never derives Intensity or
 * points from hours, and never triggers a Fit Score or Capacity recalc.
 */

import { useState } from "react";
import { ErrorState } from "@/components/shared/error-state";
import { Skeleton } from "@/components/ui/skeleton";
import type { SolutionWorkloadMonth } from "@/hooks/use-solution-workload";
import { useSolutionWorkload } from "@/hooks/use-solution-workload";

const MONTH_ABBR = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
] as const;

/**
 * Story 4 AC: "where guidance or actuals fall materially below or above the
 * benchmark, the gap is visually indicated." 20% is a sensible materiality
 * threshold — smaller drifts are normal staffing noise, not a signal worth a
 * visual flag. Named so the sensitivity is a single edit, not a magic number
 * scattered through the render logic.
 */
const WORKLOAD_DEVIATION_THRESHOLD = 0.2;

const CHART_W = 640;
const CHART_H = 220;
const PAD_LEFT = 36;
const PAD_RIGHT = 12;
const PAD_TOP = 16;
const PAD_BOTTOM = 34;
const PLOT_W = CHART_W - PAD_LEFT - PAD_RIGHT;
const PLOT_H = CHART_H - PAD_TOP - PAD_BOTTOM;

/** Horizontal gridline fractions of yMax — also the Y-axis hour-tick positions. */
const Y_TICKS = [0, 0.25, 0.5, 0.75, 1] as const;

type Deviation = "under" | "over" | "none";

/** Pure: classify a month's hours against its benchmark. null hours = "none" (nothing to flag). */
function classifyDeviation(hours: number | null, expectedHours: number): Deviation {
  if (hours === null || expectedHours <= 0) return "none";
  const ratio = hours / expectedHours;
  if (ratio <= 1 - WORKLOAD_DEVIATION_THRESHOLD) return "under";
  if (ratio >= 1 + WORKLOAD_DEVIATION_THRESHOLD) return "over";
  return "none";
}

/** Parse "YYYY-MM" into a UTC Date at day 1 — pure, no Date.now() dependency. */
function monthKeyToDate(key: string): Date {
  const [yearStr, monthStr] = key.split("-");
  const year = Number(yearStr);
  const month1 = Number(monthStr);
  return new Date(Date.UTC(year, month1 - 1, 1));
}

function monthLabel(key: string): { abbr: string; year: number } {
  const d = monthKeyToDate(key);
  const abbr = MONTH_ABBR[d.getUTCMonth()] ?? "";
  return { abbr, year: d.getUTCFullYear() };
}

/** Tiny inline relative-time formatter — no helper exists yet in src/lib or src/hooks (checked). */
function relativeTimeFrom(iso: string, now: Date): string {
  const then = new Date(iso);
  const diffMs = now.getTime() - then.getTime();
  const diffMin = Math.round(diffMs / 60_000);
  if (diffMin < 1) return "just now";
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHr = Math.round(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h ago`;
  const diffDay = Math.round(diffHr / 24);
  if (diffDay < 30) return `${diffDay}d ago`;
  const diffMonth = Math.round(diffDay / 30);
  if (diffMonth < 12) return `${diffMonth}mo ago`;
  const diffYear = Math.round(diffMonth / 12);
  return `${diffYear}y ago`;
}

/** Data considered stale past 30 days without a sync — matches the ~monthly aggregation cadence. */
const STALE_AFTER_MS = 30 * 24 * 60 * 60 * 1000;

function x(index: number, count: number): number {
  if (count <= 1) return PAD_LEFT + PLOT_W / 2;
  return PAD_LEFT + (index / (count - 1)) * PLOT_W;
}

function yFor(hours: number, maxHours: number): number {
  if (maxHours <= 0) return PAD_TOP + PLOT_H;
  const frac = Math.min(1, hours / maxHours);
  return PAD_TOP + PLOT_H * (1 - frac);
}

const BAR_W = 22;

function WorkloadSkeleton() {
  return (
    <div className="space-y-3">
      <Skeleton className="h-4 w-56" />
      <Skeleton className="h-[220px] w-full" />
      <Skeleton className="h-4 w-72" />
    </div>
  );
}

/** Whether this "YYYY-MM" key names the caller's current UTC calendar month. */
function isSameYearMonth(key: string): boolean {
  const now = new Date();
  const d = monthKeyToDate(key);
  return d.getUTCFullYear() === now.getUTCFullYear() && d.getUTCMonth() === now.getUTCMonth();
}

/**
 * Keep the floating tooltip inside the chart card: anchor its left edge near
 * the column on the left third, its right edge near the column on the right
 * third, and center it in the middle. Percentages are relative to the
 * tooltip's own width, so this is width-agnostic.
 */
function tooltipTransform(leftPct: number): string {
  if (leftPct < 28) return "translateX(-12px)";
  if (leftPct > 72) return "translateX(calc(-100% + 12px))";
  return "translateX(-50%)";
}

/** Hover breakdown for one month: total, per-Resource rows, Intensity + benchmark. */
function WorkloadTooltip({ month }: { month: SolutionWorkloadMonth }) {
  const { abbr, year } = monthLabel(month.month);
  const isCurrent = month.kind === "guidance" && isSameYearMonth(month.month);
  const isActual = month.kind === "actual";
  const total = isActual ? month.actualHours : month.guidanceHours;
  const rows = isActual ? month.actualsByResource : month.guidanceByResource;
  const seriesLabel = isActual ? "Actuals" : "Guidance";
  const deviation = classifyDeviation(total, month.expectedHours);
  const deviationText =
    deviation === "under" ? " · under-served" : deviation === "over" ? " · over-served" : "";
  return (
    <div className="min-w-[172px] max-w-[220px] rounded-md border border-border bg-card px-2.5 py-2 text-[11px] shadow-md">
      <div className="font-semibold text-foreground">
        {abbr} {year}
        {isCurrent ? " (guidance)" : ""}
      </div>
      <div className="mt-0.5 flex items-center justify-between gap-3">
        <span className="text-muted-foreground">{seriesLabel}</span>
        <span className="font-semibold text-foreground">
          {total === null ? "no data" : `${Math.round(total)} hrs`}
        </span>
      </div>
      {rows.length > 0 ? (
        <div className="mt-1.5 space-y-0.5 border-t border-border pt-1.5">
          {rows.map((r) => (
            <div key={r.resourceId} className="flex items-center justify-between gap-3">
              <span className="truncate text-muted-foreground">{r.name}</span>
              <span className="shrink-0 tabular-nums text-foreground">
                {Math.round(r.hours)} hrs
              </span>
            </div>
          ))}
        </div>
      ) : (
        <div className="mt-1 text-[10px] text-muted-foreground/70 italic">
          {isActual ? "No logged hours" : "No per-Resource guidance"}
        </div>
      )}
      <div className="mt-1.5 border-t border-border pt-1.5 text-[10px] text-muted-foreground">
        Intensity {month.intensity} · Benchmark {Math.round(month.expectedHours)} hrs
        {deviationText}
      </div>
    </div>
  );
}

export function SolutionWorkloadChart({ solutionId }: { solutionId: string }) {
  const { data, isLoading, isError, refetch } = useSolutionWorkload(solutionId);
  const [hoverIndex, setHoverIndex] = useState<number | null>(null);

  if (isLoading) return <WorkloadSkeleton />;

  if (isError) {
    return <ErrorState message="Couldn't load the workload chart" onRetry={() => refetch()} />;
  }

  if (!data) {
    return <ErrorState message="Couldn't load the workload chart" onRetry={() => refetch()} />;
  }

  const { months, hoursPerIntensityPoint, actualsEmpty, guidanceUnavailable, lastSyncedAt } = data;
  const observedSupporting = data.observedSupporting;

  // months is documented as exactly 13 entries; guard defensively for
  // noUncheckedIndexedAccess rather than assert the shape.
  if (months.length === 0) {
    return (
      <div className="rounded-md border border-dashed border-border bg-card/50 px-6 py-10 text-center text-[12px] text-muted-foreground">
        No workload data available for this Solution yet.
      </div>
    );
  }

  const firstGuidanceIndex = months.findIndex((m) => m.kind === "guidance");
  const todayDividerX =
    firstGuidanceIndex > 0
      ? (x(firstGuidanceIndex - 1, months.length) + x(firstGuidanceIndex, months.length)) / 2
      : null;
  // Percent of chart width that is the actuals region: 0% if every month is
  // guidance (no divider before it), 100% if every month is actual (no
  // guidance months exist), otherwise the divider's x-position as a percent.
  const boundaryPct =
    firstGuidanceIndex === 0
      ? 0
      : firstGuidanceIndex === -1
        ? 100
        : ((todayDividerX ?? CHART_W) / CHART_W) * 100;

  const maxHours = months.reduce((max, m) => {
    const candidate = Math.max(m.actualHours ?? 0, m.guidanceHours ?? 0, m.expectedHours);
    return Math.max(max, candidate);
  }, 1);
  // Headroom so bars/benchmark never touch the plot ceiling.
  const yMax = maxHours * 1.15;

  const benchmarkPoints = months
    .map((m, i) => `${x(i, months.length)},${yFor(m.expectedHours, yMax)}`)
    .join(" ");

  const colW = PLOT_W / months.length;
  const hovered = hoverIndex !== null ? months[hoverIndex] : undefined;
  const hoverLeftPct = hoverIndex !== null ? (x(hoverIndex, months.length) / CHART_W) * 100 : 0;

  const now = new Date();

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
          Guidance
        </div>
        {lastSyncedAt ? (
          <SyncBadge lastSyncedAt={lastSyncedAt} now={now} />
        ) : (
          <span
            className="text-[10px] text-muted-foreground"
            title="No Hours Entry sync has run yet for this Solution's linked Projects."
          >
            never synced
          </span>
        )}
      </div>

      <div className="relative rounded-md border border-border bg-card p-2">
        <svg
          viewBox={`0 0 ${CHART_W} ${CHART_H}`}
          className="h-56 w-full"
          onMouseLeave={() => setHoverIndex(null)}
        >
          <title>Solution workload — actuals, guidance, and Intensity benchmark by month</title>
          <defs>
            <pattern
              id={`guidance-hatch-${solutionId}`}
              width="6"
              height="6"
              patternTransform="rotate(45)"
              patternUnits="userSpaceOnUse"
            >
              <rect width="6" height="6" fill="var(--intensity-6)" fillOpacity="0.18" />
              <line x1="0" y1="0" x2="0" y2="6" stroke="var(--intensity-6)" strokeWidth="2" />
            </pattern>
          </defs>

          {/* Y-axis: horizontal gridlines + hour-value tick labels. */}
          <text
            x={PAD_LEFT - 5}
            y={PAD_TOP - 5}
            fontSize="8"
            fill="var(--muted-foreground)"
            textAnchor="end"
            style={{ fontFamily: "var(--font-display)" }}
          >
            hrs
          </text>
          {Y_TICKS.map((frac) => {
            const gy = PAD_TOP + PLOT_H * (1 - frac);
            return (
              <g key={frac}>
                <line
                  x1={PAD_LEFT}
                  y1={gy}
                  x2={CHART_W - PAD_RIGHT}
                  y2={gy}
                  stroke="var(--border)"
                  strokeWidth="1"
                  strokeDasharray={frac === 0 ? "0" : "2 3"}
                />
                <text
                  x={PAD_LEFT - 5}
                  y={gy + 3}
                  fontSize="8"
                  fill="var(--muted-foreground)"
                  textAnchor="end"
                  style={{ fontFamily: "var(--font-display)" }}
                >
                  {Math.round(frac * yMax)}
                </text>
              </g>
            );
          })}

          {/* Hovered-column highlight (behind bars). */}
          {hoverIndex !== null && (
            <rect
              x={x(hoverIndex, months.length) - colW / 2}
              y={PAD_TOP}
              width={colW}
              height={PLOT_H}
              fill="var(--foreground)"
              fillOpacity="0.05"
            />
          )}

          {/* Bars: actual (solid) or guidance (hatched), per month. */}
          {months.map((m, i) => {
            const hours = m.kind === "actual" ? m.actualHours : m.guidanceHours;
            if (hours === null) return null;
            const bx = x(i, months.length) - BAR_W / 2;
            const by = yFor(hours, yMax);
            const bh = PAD_TOP + PLOT_H - by;
            const deviation = classifyDeviation(hours, m.expectedHours);
            const fill =
              m.kind === "actual" ? "var(--brand-600)" : `url(#guidance-hatch-${solutionId})`;
            const stroke = m.kind === "actual" ? "none" : "var(--intensity-6)";
            return (
              <g key={m.month}>
                <rect
                  x={bx}
                  y={by}
                  width={BAR_W}
                  height={Math.max(0, bh)}
                  fill={fill}
                  stroke={stroke}
                  strokeWidth={m.kind === "guidance" ? 1 : 0}
                  strokeDasharray={m.kind === "guidance" ? "3 2" : undefined}
                  rx="2"
                />
                {deviation !== "none" && (
                  <circle
                    cx={x(i, months.length)}
                    cy={by - 6}
                    r="3.5"
                    fill={deviation === "under" ? "var(--fit-at-risk)" : "var(--fit-moderate)"}
                    stroke="var(--card)"
                    strokeWidth="1"
                  />
                )}
              </g>
            );
          })}

          {/* Story 4: Intensity benchmark overlay, spanning both regions. */}
          <polyline
            points={benchmarkPoints}
            fill="none"
            stroke="var(--intensity-8)"
            strokeWidth="2"
            strokeLinejoin="round"
          />
          {months.map((m, i) => (
            <circle
              key={`benchmark-${m.month}`}
              cx={x(i, months.length)}
              cy={yFor(m.expectedHours, yMax)}
              r="2.5"
              fill="var(--intensity-8)"
            />
          ))}
          {/* Benchmark hours labeled on the line — at the first bucket and wherever
              the level steps (a scheduled Intensity change), so the value is
              readable without hovering, without cluttering every point. */}
          {months.map((m, i) => {
            const prev = i > 0 ? months[i - 1] : undefined;
            const isLevelStart =
              i === 0 || (prev !== undefined && prev.expectedHours !== m.expectedHours);
            if (!isLevelStart) return null;
            const bx = x(i, months.length);
            const by = yFor(m.expectedHours, yMax);
            return (
              <text
                key={`benchmark-label-${m.month}`}
                x={i === 0 ? bx + 3 : bx}
                y={by - 6}
                fontSize="8.5"
                fill="var(--intensity-8)"
                textAnchor={i === 0 ? "start" : "middle"}
                style={{ fontFamily: "var(--font-display)", fontWeight: 700 }}
              >
                {Math.round(m.expectedHours)} hrs
              </text>
            );
          })}

          {/* "Today" divider — actuals→guidance boundary. */}
          {todayDividerX !== null && (
            <g>
              <line
                x1={todayDividerX}
                y1={PAD_TOP - 4}
                x2={todayDividerX}
                y2={PAD_TOP + PLOT_H}
                stroke="var(--foreground)"
                strokeWidth="1"
                strokeDasharray="4 3"
                opacity="0.6"
              />
              <text
                x={todayDividerX}
                y={PAD_TOP - 6}
                fontSize="8"
                fill="var(--muted-foreground)"
                textAnchor="middle"
                style={{ fontFamily: "var(--font-display)", fontWeight: 600 }}
              >
                Today
              </text>
            </g>
          )}

          {/* Month axis labels. Year shown at year boundaries (Jan) or the first bucket. */}
          {months.map((m, i) => {
            const { abbr, year } = monthLabel(m.month);
            const showYear = abbr === "Jan" || i === 0;
            const isCurrent = firstGuidanceIndex === i;
            return (
              <g key={`axis-${m.month}`}>
                <text
                  x={x(i, months.length)}
                  y={CHART_H - PAD_BOTTOM + 14}
                  fontSize="9"
                  fill={isCurrent ? "var(--foreground)" : "var(--muted-foreground)"}
                  fontWeight={isCurrent ? 700 : 400}
                  textAnchor="middle"
                  style={{ fontFamily: "var(--font-display)" }}
                >
                  {abbr}
                  {showYear ? ` '${String(year).slice(2)}` : ""}
                </text>
                {isCurrent && (
                  <text
                    x={x(i, months.length)}
                    y={CHART_H - PAD_BOTTOM + 25}
                    fontSize="7"
                    fill="var(--intensity-6)"
                    textAnchor="middle"
                    style={{ fontFamily: "var(--font-display)" }}
                  >
                    (guidance)
                  </text>
                )}
              </g>
            );
          })}

          {/* Transparent per-column hover targets — rendered last so they sit on
              top and capture the pointer over bars, benchmark, and gaps alike. */}
          {months.map((m, i) => (
            <rect
              key={`hover-${m.month}`}
              x={x(i, months.length) - colW / 2}
              y={PAD_TOP}
              width={colW}
              height={PLOT_H}
              fill="transparent"
              onMouseEnter={() => setHoverIndex(i)}
              style={{ cursor: "pointer" }}
            />
          ))}
        </svg>

        {/* Per-month hover breakdown (Story 5). Positioned by the hovered
            column's horizontal fraction; vertical near the top of the plot. */}
        {hovered && (
          <div
            className="pointer-events-none absolute z-10 top-1"
            style={{ left: `${hoverLeftPct}%`, transform: tooltipTransform(hoverLeftPct) }}
          >
            <WorkloadTooltip month={hovered} />
          </div>
        )}

        {/* Empty/degraded overlays — never render as a zero line. The actuals
            region spans [0, boundaryPct]; the guidance region spans
            [boundaryPct, 100]. boundaryPct is the "today" divider position (or
            the full width when every month is one kind). */}
        {actualsEmpty && (
          <div
            className="pointer-events-none absolute left-0 top-2 flex items-start justify-center px-2 text-center"
            style={{ width: `${boundaryPct}%` }}
          >
            <span className="rounded bg-card/90 px-2 py-1 text-[10px] text-muted-foreground shadow-sm">
              No linked NOS demands — run the demand sync in Settings → Integrations.
            </span>
          </div>
        )}
        {guidanceUnavailable && (
          <div
            className="pointer-events-none absolute top-2 flex items-start justify-center px-2 text-center"
            style={{ left: `${boundaryPct}%`, right: 0 }}
          >
            <span className="rounded bg-card/90 px-2 py-1 text-[10px] text-muted-foreground shadow-sm">
              Guidance unavailable — showing benchmark only.
            </span>
          </div>
        )}
      </div>

      <Legend hoursPerIntensityPoint={hoursPerIntensityPoint} />

      <p className="text-[10px] text-muted-foreground">
        The Intensity benchmark is display-only — it never affects Assignment points or Fit Score.
        Guidance reflects every Resource NOS forecasts against this Solution's linked Demands,
        whether or not they're assigned to the Pod — so it shows the Demand's full forward demand,
        not just the currently-staffed slice.
      </p>

      {observedSupporting.length > 0 && (
        <div className="rounded-md border border-border bg-card/50 px-3 py-2">
          <p className="mb-1 text-[10px] font-medium text-foreground">
            Observed on logged time — not assigned
          </p>
          <ul className="flex flex-wrap gap-x-3 gap-y-1 text-[10px] text-muted-foreground">
            {observedSupporting.map((r) => (
              <li key={r.resourceId}>
                {r.name} · {r.hours.toLocaleString()} hrs
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function SyncBadge({ lastSyncedAt, now }: { lastSyncedAt: string; now: Date }) {
  const then = new Date(lastSyncedAt);
  const isStale = now.getTime() - then.getTime() > STALE_AFTER_MS;
  return (
    <span
      className={`text-[10px] ${isStale ? "text-[var(--fit-moderate)]" : "text-muted-foreground"}`}
      title={`Actuals last synced ${then.toLocaleString()}${isStale ? " — this data may be stale" : ""}`}
    >
      synced {relativeTimeFrom(lastSyncedAt, now)}
      {isStale ? " (stale)" : ""}
    </span>
  );
}

function Legend({ hoursPerIntensityPoint }: { hoursPerIntensityPoint: number }) {
  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-[10px] text-muted-foreground">
      <span className="flex items-center gap-1.5">
        <span className="inline-block h-2.5 w-2.5 rounded-sm bg-brand-600" />
        Actuals
      </span>
      <span className="flex items-center gap-1.5">
        <span
          className="inline-block h-2.5 w-2.5 rounded-sm border border-dashed border-[var(--intensity-6)]"
          style={{
            backgroundImage:
              "repeating-linear-gradient(45deg, var(--intensity-6) 0, var(--intensity-6) 1px, transparent 1px, transparent 4px)",
            opacity: 0.7,
          }}
        />
        <span title="Sum of NOS-sourced forward guidance across every Resource NOS forecasts against this Solution's linked Demands — assigned to the Pod or not.">
          Guidance
        </span>
      </span>
      <span className="flex items-center gap-1.5">
        <span className="inline-block h-0.5 w-3.5 rounded-full bg-[var(--intensity-8)]" />
        Intensity benchmark
      </span>
      <span className="flex items-center gap-1.5">
        <span className="inline-block h-2 w-2 rounded-full bg-[var(--fit-at-risk)]" />
        Under-served
      </span>
      <span className="flex items-center gap-1.5">
        <span className="inline-block h-2 w-2 rounded-full bg-[var(--fit-moderate)]" />
        Over-served
      </span>
      <span className="ml-auto font-medium text-foreground">
        Benchmark = Intensity × {hoursPerIntensityPoint} hrs
      </span>
    </div>
  );
}
